import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { User } from "firebase/auth";
import { createLogger } from "../utils/logger";

const logger = createLogger("AnalyticsService");

// Error codes for easier reporting and debugging
export const ANALYTICS_ERROR_CODES = {
  // Authentication errors
  AUTH_LOGIN_FAILED: "AUTH_001",
  AUTH_REGISTRATION_FAILED: "AUTH_002",
  AUTH_LOGOUT_FAILED: "AUTH_003",
  AUTH_TOKEN_REFRESH_FAILED: "AUTH_004",
  AUTH_GOOGLE_OAUTH_FAILED: "AUTH_005",
  AUTH_VERIFICATION_FAILED: "AUTH_006",
  AUTH_PASSWORD_RESET_FAILED: "AUTH_007",

  // API errors
  API_CALL_FAILED: "API_001",
  API_TIMEOUT: "API_002",
  API_NETWORK_ERROR: "API_003",
  API_UNAUTHORIZED: "API_004",
  API_FORBIDDEN: "API_005",
  API_NOT_FOUND: "API_006",
  API_SERVER_ERROR: "API_007",

  // Google Services errors
  GOOGLE_CALENDAR_CONNECTION_FAILED: "GOOGLE_001",
  GOOGLE_DRIVE_CONNECTION_FAILED: "GOOGLE_002",
  GOOGLE_EMAIL_CONNECTION_FAILED: "GOOGLE_003",
  GOOGLE_PERMISSIONS_DENIED: "GOOGLE_004",
  GOOGLE_QUOTA_EXCEEDED: "GOOGLE_005",

  // Chat/AI errors
  CHAT_MESSAGE_SEND_FAILED: "CHAT_001",
  CHAT_AGENT_RESPONSE_FAILED: "CHAT_002",
  CHAT_THREAD_LOAD_FAILED: "CHAT_003",
  CHAT_AGENT_INITIALIZATION_FAILED: "CHAT_004",

  // Calendar errors
  CALENDAR_EVENT_CREATE_FAILED: "CAL_001",
  CALENDAR_EVENT_UPDATE_FAILED: "CAL_002",
  CALENDAR_EVENT_DELETE_FAILED: "CAL_003",
  CALENDAR_SYNC_FAILED: "CAL_004",

  // Todo errors
  TODO_CREATE_FAILED: "TODO_001",
  TODO_UPDATE_FAILED: "TODO_002",
  TODO_DELETE_FAILED: "TODO_003",
  TODO_SYNC_FAILED: "TODO_004",

  // Onboarding errors
  ONBOARDING_STEP_FAILED: "ONB_001",
  ONBOARDING_USER_INFO_SAVE_FAILED: "ONB_002",
  ONBOARDING_GOOGLE_CONNECTION_FAILED: "ONB_003",

  // General errors
  COMPONENT_ERROR: "GEN_001",
  NAVIGATION_ERROR: "GEN_002",
  PERMISSION_ERROR: "GEN_003",
  VALIDATION_ERROR: "GEN_004",
  UNKNOWN_ERROR: "GEN_999",
} as const;

// Event types for type safety
export interface AnalyticsEvent {
  name: string;
  parameters?: Record<string, string | number | boolean>;
}

export interface ErrorEvent extends AnalyticsEvent {
  name: "error_occurred";
  parameters: {
    error_code: string;
    error_message: string;
    error_stack?: string;
    component?: string;
    user_action?: string;
    severity: "low" | "medium" | "high" | "critical";
  };
}

export interface AuthEvent extends AnalyticsEvent {
  name: "auth_event";
  parameters: {
    event_type: "login" | "logout" | "register" | "login_failed" | "register_failed" | "verification_email_sent" | "password_reset_sent";
    method: "email" | "google" | "unknown";
    user_id?: string;
    error_code?: string;
  };
}

export interface NavigationEvent extends AnalyticsEvent {
  name: "page_view";
  parameters: {
    page_title: string;
    page_location: string;
    page_path: string;
    user_id?: string;
  };
}

export interface UserInteractionEvent extends AnalyticsEvent {
  name: "user_interaction";
  parameters: {
    interaction_type: string;
    component: string;
    action: string;
    value?: string | number;
    user_id?: string;
  };
}

export interface OnboardingEvent extends AnalyticsEvent {
  name: "onboarding_event";
  parameters: {
    step: string;
    action: "started" | "completed" | "skipped" | "failed";
    step_number?: number;
    total_steps?: number;
    user_id?: string;
    error_code?: string;
  };
}

export interface ChatEvent extends AnalyticsEvent {
  name: "chat_event";
  parameters: {
    event_type: "message_sent" | "message_received" | "session_started" | "session_ended" | "agent_selected";
    agent_id?: string;
    message_length?: number;
    response_time?: number;
    user_id?: string;
    error_code?: string;
  };
}

export interface CalendarEvent extends AnalyticsEvent {
  name: "calendar_event";
  parameters: {
    event_type: "event_created" | "event_updated" | "event_deleted" | "calendar_synced" | "view_changed";
    event_duration?: number;
    calendar_type?: string;
    user_id?: string;
    error_code?: string;
  };
}

export interface TodoEvent extends AnalyticsEvent {
  name: "todo_event";
  parameters: {
    event_type: "todo_created" | "todo_completed" | "todo_updated" | "todo_deleted" | "priority_set";
    todo_category?: string;
    priority_level?: string;
    user_id?: string;
    error_code?: string;
  };
}

export interface TutorialEvent extends AnalyticsEvent {
  name: "tutorial_event";
  parameters: {
    event_type: "tutorial_started" | "tutorial_completed" | "tutorial_skipped" | "tutorial_restarted";
    tutorial_id: string;
    tutorial_name?: string;
    step_count?: number;
    completion_type?: "seen" | "completed" | "skipped";
    skip_type?: "all_tutorials" | "single_tutorial";
    user_id?: string;
  };
}

class AnalyticsService {
  private isEnabled = false;
  private currentUser: User | null = null;

  constructor() {
    this.checkAnalyticsAvailability();
  }

  private checkAnalyticsAvailability(): void {
    // Check if we're in a browser environment and analytics is available
    try {
      // Check if Firebase Analytics is available globally or can be imported
      if (typeof window !== "undefined" && (window as any).gtag) {
        this.isEnabled = true;
        logger.info("Analytics service initialized and enabled (gtag available)");
      } else {
        // Try to check if Firebase Analytics is available
        this.isEnabled = false;
        logger.info("Analytics service initialized but disabled (no gtag)");
      }
    } catch (error) {
      logger.warn("Analytics service initialization failed:", error);
      this.isEnabled = false;
    }
  }

  /**
   * Set the current user for analytics tracking
   */
  setUser(user: User | null): void {
    this.currentUser = user;

    if (this.isEnabled && user) {
      try {
        // Dynamic import to avoid circular dependency
        const { analytics } = require("./firebase");
        if (!analytics) {
          logger.debug("Analytics not available for setUser");
          return;
        }

        setUserId(analytics, user.uid);
        // Only set basic user properties here - detailed properties handled by UserPropertiesService
        setUserProperties(analytics, {
          user_id: user.uid,
          email: user.email,
          display_name: user.displayName,
          auth_provider: user.providerData[0]?.providerId || "unknown",
          account_created: user.metadata.creationTime,
          last_sign_in: user.metadata.lastSignInTime,
        });
        logger.debug("Basic user properties set for analytics");
      } catch (error) {
        logger.error("Failed to set user properties:", error);
      }
    }
  }

  /**
   * Log a custom event
   */
  logEvent(event: AnalyticsEvent): void {
    if (!this.isEnabled) {
      logger.debug("Analytics disabled, skipping event:", event.name);
      return;
    }

    try {
      // Dynamic import to avoid circular dependency
      const { analytics } = require("./firebase");
      if (!analytics) {
        logger.debug("Analytics not available, skipping event:", event.name);
        return;
      }

      // Add user_id to all events if available
      const eventWithUser = {
        ...event,
        parameters: {
          ...event.parameters,
          user_id: this.currentUser?.uid,
        },
      };

      logEvent(analytics, eventWithUser.name, eventWithUser.parameters);
      logger.debug("Analytics event logged:", eventWithUser.name, eventWithUser.parameters);
    } catch (error) {
      logger.error("Failed to log analytics event:", error);
    }
  }

  /**
   * Log an error event with standardized error codes
   */
  logError(
    errorCode: string,
    errorMessage: string,
    options: {
      errorStack?: string;
      component?: string;
      userAction?: string;
      severity?: "low" | "medium" | "high" | "critical";
    } = {}
  ): void {
    const errorEvent: ErrorEvent = {
      name: "error_occurred",
      parameters: {
        error_code: errorCode,
        error_message: errorMessage,
        error_stack: options.errorStack,
        component: options.component,
        user_action: options.userAction,
        severity: options.severity || "medium",
      },
    };

    this.logEvent(errorEvent);

    // Also log to console for development
    logger.error(`Analytics Error [${errorCode}]:`, errorMessage, options);
  }

  /**
   * Log authentication events
   */
  logAuthEvent(eventType: AuthEvent["parameters"]["event_type"], method: AuthEvent["parameters"]["method"], errorCode?: string): void {
    const authEvent: AuthEvent = {
      name: "auth_event",
      parameters: {
        event_type: eventType,
        method,
        user_id: this.currentUser?.uid,
        error_code: errorCode,
      },
    };

    this.logEvent(authEvent);
  }

  /**
   * Log page view events
   */
  logPageView(pageTitle: string, pagePath: string): void {
    const pageEvent: NavigationEvent = {
      name: "page_view",
      parameters: {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: pagePath,
        user_id: this.currentUser?.uid,
      },
    };

    this.logEvent(pageEvent);
  }

  /**
   * Log user interaction events
   */
  logUserInteraction(interactionType: string, component: string, action: string, value?: string | number): void {
    const interactionEvent: UserInteractionEvent = {
      name: "user_interaction",
      parameters: {
        interaction_type: interactionType,
        component,
        action,
        value,
        user_id: this.currentUser?.uid,
      },
    };

    this.logEvent(interactionEvent);
  }

  /**
   * Log onboarding events
   */
  logOnboardingEvent(step: string, action: OnboardingEvent["parameters"]["action"], stepNumber?: number, totalSteps?: number, errorCode?: string): void {
    const onboardingEvent: OnboardingEvent = {
      name: "onboarding_event",
      parameters: {
        step,
        action,
        step_number: stepNumber,
        total_steps: totalSteps,
        user_id: this.currentUser?.uid,
        error_code: errorCode,
      },
    };

    this.logEvent(onboardingEvent);
  }

  /**
   * Log chat events
   */
  logChatEvent(
    eventType: ChatEvent["parameters"]["event_type"],
    options: {
      agentId?: string;
      messageLength?: number;
      responseTime?: number;
      errorCode?: string;
    } = {}
  ): void {
    const chatEvent: ChatEvent = {
      name: "chat_event",
      parameters: {
        event_type: eventType,
        agent_id: options.agentId,
        message_length: options.messageLength,
        response_time: options.responseTime,
        user_id: this.currentUser?.uid,
        error_code: options.errorCode,
      },
    };

    this.logEvent(chatEvent);
  }

  /**
   * Log calendar events
   */
  logCalendarEvent(
    eventType: CalendarEvent["parameters"]["event_type"],
    options: {
      eventDuration?: number;
      calendarType?: string;
      errorCode?: string;
    } = {}
  ): void {
    const calendarEvent: CalendarEvent = {
      name: "calendar_event",
      parameters: {
        event_type: eventType,
        event_duration: options.eventDuration,
        calendar_type: options.calendarType,
        user_id: this.currentUser?.uid,
        error_code: options.errorCode,
      },
    };

    this.logEvent(calendarEvent);
  }

  /**
   * Log todo events
   */
  logTodoEvent(
    eventType: TodoEvent["parameters"]["event_type"],
    options: {
      todoCategory?: string;
      priorityLevel?: string;
      errorCode?: string;
    } = {}
  ): void {
    const todoEvent: TodoEvent = {
      name: "todo_event",
      parameters: {
        event_type: eventType,
        todo_category: options.todoCategory,
        priority_level: options.priorityLevel,
        user_id: this.currentUser?.uid,
        error_code: options.errorCode,
      },
    };

    this.logEvent(todoEvent);
  }

  /**
   * Log tutorial events
   */
  logTutorialEvent(
    eventType: TutorialEvent["parameters"]["event_type"],
    options: {
      tutorialId: string;
      tutorialName?: string;
      stepCount?: number;
      completionType?: "seen" | "completed" | "skipped";
      skipType?: "all_tutorials" | "single_tutorial";
    }
  ): void {
    const tutorialEvent: TutorialEvent = {
      name: "tutorial_event",
      parameters: {
        event_type: eventType,
        tutorial_id: options.tutorialId,
        tutorial_name: options.tutorialName,
        step_count: options.stepCount,
        completion_type: options.completionType,
        skip_type: options.skipType,
        user_id: this.currentUser?.uid,
      },
    };

    this.logEvent(tutorialEvent);
  }

  /**
   * Set custom user properties for segmentation
   */
  setUserProperty(key: string, value: string | number | boolean): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      // Dynamic import to avoid circular dependency
      const { analytics } = require("./firebase");
      if (!analytics) {
        logger.debug("Analytics not available for setUserProperty");
        return;
      }

      setUserProperties(analytics, { [key]: value });
      logger.debug("User property set:", key, value);
    } catch (error) {
      logger.error("Failed to set user property:", error);
    }
  }

  /**
   * Check if analytics is enabled
   */
  isAnalyticsEnabled(): boolean {
    return this.isEnabled;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
